import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SectionTitle from "./SectionTitle";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "../data/api";

const types = [
  { value: "booking", label: "Booking / Date" },
  { value: "festival", label: "Festival" },
  { value: "collab", label: "Collaboration" },
  { value: "press", label: "Presse / Media" },
  { value: "other", label: "Autre" },
];

const empty = { name: "", email: "", subject: "", type: "booking", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé !", { description: "L'équipe d'Oli.Vero vous répondra rapidement." });
      setForm(empty);
    } catch {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section bg-gradient-to-b from-[#050505] to-[#08060f] py-28">
      <div className="orb orb-blue" style={{ width: 420, height: 420, bottom: "0%", right: "-8%" }} />
      <div className="mx-auto max-w-4xl px-5 lg:px-8">
        <div className="text-center">
          <SectionTitle kicker="Contact" title="Booking & collaborations" testid="contact-title" />
        </div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass mx-auto rounded-3xl p-6 sm:p-10"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Input data-testid="contact-name" placeholder="Nom" value={form.name} onChange={set("name")}
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40" />
            <Input data-testid="contact-email" type="email" placeholder="Email" value={form.email} onChange={set("email")}
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40" />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Input data-testid="contact-subject" placeholder="Sujet" value={form.subject} onChange={set("subject")}
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40" />
            <Select value={form.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="contact-type" className="border-white/10 bg-white/5 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#0c0c12] text-white">
                {types.map((t) => (
                  <SelectItem key={t.value} value={t.value} data-testid={`contact-type-${t.value}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea data-testid="contact-message" placeholder="Votre message..." rows={5} value={form.message} onChange={set("message")}
            className="mt-4 border-white/10 bg-white/5 text-white placeholder:text-white/40" />

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="btn-glow mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-electric to-neon py-4 font-heading text-base font-semibold text-white disabled:opacity-60"
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
            {loading ? "Envoi..." : "Envoyer la demande"}
          </button>
          <p className="mt-4 text-center font-body text-xs text-white/40">
            management@oli.vero · Réponse sous 48h ouvrées
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
